import React, { useCallback, useContext, useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { StockLot } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import LinkFormatter from '../../components/grid-addons/formatters/LinkFormatter';
import attributeQuery from '../../graphql/queries/product/LotAttributeConfigListQuery.graphql';
import { ALLOW_VIEW } from '../../utils/OperationPermissions';
import getProductDP from '../../utils/getProductDP';
import { useWorkspace, WorkspaceConfig } from '../../graphql/hooks/useWorkspace';
import { useLotAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import { Paths } from '../../@types/keyOfNested';
import {
  CostViewPermission,
  IProductWarehouseDetail,
  IStockLot,
  Maybe,
  FilterOperation,
} from '../../graphql/schema/p4types';
import AppContext from '../../components/Context/AppContext';
import { IFormatterProps, IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import BinLocationFormatter from '../../components/grid-addons/formatters/BinLocationFormatter';
import { formatDate } from '../../utils/DateHelper';
import { IEditableDataGridColumnDef, IEditableDataGridNumericOptions } from '../../@types/editableDataGridTypes';
import { ICustomColumn } from '../../utils/setColumnsFromArray';
import { usePrecision } from '../../utils/hooks';
import { EntityType } from '../../@types/entityTypes';

const PRODUCTS_STOCK_LOT_FIELD_NAMES: Array<Paths<IStockLot, 3>> = [
  'stock.product.reference',
  'stock.binLocation.warehouse.description',
  'stock.binLocation.location',
  'level',
  'freeStock',
  'allocated',
  'issued',
  'id' as Paths<IStockLot>,
];

const precisionCb = (rowData: IStockLot) => getProductDP(rowData, 'stock.product');

export const createWarehouseDateFormatter = (columnName: string, fieldName: string): ICustomColumn => {
  return {
    name: columnName,
    fieldName,
    type: 'LocalDate',
    sortable: false,
    formatter: ({ dependentValues }: IFormatterProps<IStockLot>) => {
      const rowData = dependentValues.rowData;
      const productWarehouseDetails = rowData?.stock?.product?.productWarehouseDetails ?? [];
      const thisWarehouseDetail = productWarehouseDetails.find(
        (pwd) => pwd?.warehouse?.id === rowData.stock?.binLocation?.warehouse?.id
      );
      if (thisWarehouseDetail) {
        const warehouseDetailValue = thisWarehouseDetail[fieldName as keyof IProductWarehouseDetail];

        if (warehouseDetailValue) {
          return formatDate(new Date(warehouseDetailValue));
        }
      }
      return '';
    },
  };
};

const ProductsStockLotWorkspace = (
  props: WorkspaceConfig<IStockLot> & {
    precision?: number;
    columns?: Array<Paths<IStockLot>>;
    nonReplenishable?: Maybe<boolean>;
    includeGenericSearch?: boolean;
  }
) => {
  const lotAttributeColumns = useLotAttributesForWorkspace(attributeQuery);
  const costPricePrecision = usePrecision('cost');

  const customColumns = useMemo<Maybe<{ [key: string]: ICustomColumn }>>(() => {
    const lastIssueDateColumn = createWarehouseDateFormatter('Last Issue', 'dateOfLastIssue');
    const lastReceiptDateColumn = createWarehouseDateFormatter('Last Receipt', 'dateOfLastReceipt');
    const lastMovementDateColumn = createWarehouseDateFormatter('Last Movement', 'dateOfLastMovement');

    return {
      ...lotAttributeColumns,
      lastIssueDateColumn,
      lastReceiptDateColumn,
      lastMovementDateColumn,
    };
  }, [lotAttributeColumns]);
  const appContext = useContext(AppContext);

  // Get the users permissions for displaying costs, default to most secure (None) if null or undefined.
  const costViewPermission: CostViewPermission = appContext?.fullUserDetails?.companyUser?.costViewPermission
    ? appContext?.fullUserDetails?.companyUser?.costViewPermission
    : CostViewPermission.None;

  const fieldNames = useMemo(() => {
    if (costViewPermission === CostViewPermission.All) {
      return [...PRODUCTS_STOCK_LOT_FIELD_NAMES, 'averageCostPrice'];
    }
    return PRODUCTS_STOCK_LOT_FIELD_NAMES;
  }, [costViewPermission]);

  const columnOverrides: IWorkspaceColumnOverride = useMemo(() => {
    const numericOptions = {
      format: { decimals: props.precision ?? precisionCb },
    } as IEditableDataGridNumericOptions;

    const overrides: IWorkspaceColumnOverride = {
      'stock.product.reference': {
        name: 'Product',
      },
      attachments: {
        name: 'Attachments',
        sortable: false,
        formatter: Formatters.AttachmentsFormatter,
      },
      'stock.binLocation.warehouse.description': {
        name: 'Warehouse',
      },
      'stock.binLocation.location': {
        name: 'Bin Location',
        formatter: BinLocationFormatter,
      },
      level: {
        formatter: 'numeric',
        options: numericOptions,
      },
      freeStock: {
        sortable: false,
        formatter: 'numeric',
        options: numericOptions,
      },
      allocated: {
        formatter: 'numeric',
        options: numericOptions,
      },
      issued: {
        formatter: 'numeric',
        options: numericOptions,
      },
      picked: {
        formatter: 'numeric',
        options: numericOptions,
      },
      dispatched: {
        formatter: 'numeric',
        options: numericOptions,
      },
      averageCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      levelValuation: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
    };
    return overrides;
  }, [props.precision, precisionCb, costPricePrecision]);

  const workspaceProps = useWorkspace<IStockLot>(props, StockLot, {
    columns: props.columns || (fieldNames as Array<Paths<IStockLot>>),
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: 'stock.product.reference',
    filters: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'stockLot.binLocation.archived',
          value: false,
        },
      },
    ],
  });

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }

      const fieldName = column.fieldName;

      if (!props.nonReplenishable) {
        if (fieldName === 'originalLevel') {
          return false;
        }
        if (fieldName === 'consumed') {
          return false;
        }
      }

      let showColumn: boolean =
        fieldName.includes('lotAttributeValues.attributeConfig.title.') || !fieldName.includes('lotAttributeValues');

      switch (costViewPermission) {
        case CostViewPermission.All:
          break;
        case CostViewPermission.Haggle:
        case CostViewPermission.None:
          if (
            fieldName === 'averageCostPrice' ||
            fieldName === 'allocationValuation' ||
            fieldName === 'levelValuation' ||
            fieldName === 'attachments.fileName' ||
            fieldName === 'attachments.comment'
          ) {
            showColumn = false;
          }
          break;
      }

      return showColumn;
    },
    [props.nonReplenishable, costViewPermission]
  );

  const availableFiltersFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }

      const fieldName = column.fieldName;

      let showColumn: boolean = availableColumnsFilter(column);

      if (['freeStock', 'lastIssueDateColumn', 'lastReceiptDateColumn', 'lastMovementDateColumn'].includes(fieldName)) {
        showColumn = false;
      }

      return showColumn;
    },
    [availableColumnsFilter]
  );

  return (
    <Workspace
      entity={StockLot}
      entityType={StockLot.type}
      title={StockLot.title}
      defaultQuery={StockLot.workspace}
      permittedOperations={ALLOW_VIEW}
      availableFiltersFilter={availableFiltersFilter}
      availableColumnsFilter={availableColumnsFilter}
      formUrl={StockLot.formUrl}
      {...workspaceProps}
      {...props}
    />
  );
};

export default ProductsStockLotWorkspace;
